import React, { useEffect, useMemo, useState } from 'react';
import { Admin_GetUserPhotoSasUrl } from '../../constants/adminData';
import { getPhotoSasUrl } from '@/services/api';

interface AvatarImageProps {
  profilePhotoId?: number | null;
  name?: string;
  alt?: string;
  size?: number;
  className?: string;
}

const avatarCache = new Map<number, string>();
const avatarPromiseCache = new Map<number, Promise<string>>();

const getInitials = (value: string) => {
  const name = (value || '').trim();
  if (!name) return 'U';
  const parts = name.split(/\s+/).filter(Boolean);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

const getValidImageId = (value?: number | string | null): number | null => {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
};

export function AvatarImage({ profilePhotoId, name = 'User', alt, size = 44, className = '' }: AvatarImageProps) {
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(false);

  const validId = useMemo(() => getValidImageId(profilePhotoId), [profilePhotoId]);

  useEffect(() => {
    let mounted = true;
    if (!validId) {
      setImageUrl(null);
      setError(true);
      setLoading(false);
      return;
    }

    const cached = avatarCache.get(validId);
    if (cached) {
      setImageUrl(cached);
      setError(false);
      setLoading(false);
      return;
    }

    setLoading(true);
    const existingPromise = avatarPromiseCache.get(validId);
    const promise = existingPromise
      ? existingPromise
      : getPhotoSasUrl(validId)
          .then((response) => {
            
            const url = response || '';
            if (!url) throw new Error('Avatar URL missing');
            avatarCache.set(validId, url);
            return url;
          })
          .finally(() => {
            avatarPromiseCache.delete(validId);
          });

    if (!existingPromise) {
      avatarPromiseCache.set(validId, promise);
    }

    promise
      .then((url) => {
        if (!mounted) return;
        setImageUrl(url);
        setError(false);
      })
      .catch((err) => {
        console.error('Failed to load avatar:', err);
        if (!mounted) return;
        setImageUrl(null);
        setError(true);
      })
      .finally(() => {
        if (!mounted) return;
        setLoading(false);
      });

    return () => {
      mounted = false;
    };
  }, [validId]);

  const sizeStyle = {
    width: `${size}px`,
    height: `${size}px`,
  };

  if (loading) {
    return (
      <div className={`rounded-full bg-slate-100 flex items-center justify-center ${className}`} style={sizeStyle}>
        <div className="w-4 h-4 border-2 border-slate-300 border-t-slate-500 rounded-full animate-spin" />
      </div>
    );
  }

  if (!imageUrl || error) {
    return (
      <div
        className={`rounded-full bg-slate-200 text-slate-700 font-semibold flex items-center justify-center ${className}`}
        style={sizeStyle}
        aria-label={alt || name}
      >
        {getInitials(name)}
      </div>
    );
  }

  return (
    <img
      src={imageUrl}
      alt={alt || name}
      className={`rounded-full object-cover ${className}`}
      style={sizeStyle}
      onError={() => setError(true)}
    />
  );
}
